<template>
    <div class="overview-container">
        <div v-if="getCurrentSMSCampaign" class="info-container">
            <v-card class="table-wrapper">
                <campaign-table :rows="campaignTableRows">
                    <template v-slot:file_used>
                        <v-btn
                            color="primary"
                            class="my-2 white--text"
                            @click="downloadFile"
                        >
                            Download
                            <v-icon right dark> mdi-file-download-outline </v-icon>
                        </v-btn>
                    </template>
                    <template v-slot:status>
                        <v-icon
                            v-if="getCurrentSMSCampaign.completedAt"
                            color="success"
                            class="inline-icon"
                        >
                            mdi-check-circle-outline
                        </v-icon>
                        <v-icon v-else color="orange" class="inline-icon">
                            mdi-clock-outline
                        </v-icon>
                    </template>
                </campaign-table>
                <create-form-actions
                    needs-padding
                    has-previous-step-btn
                    has-submit-btn
                    :submit-loading="submitLoading"
                    :submit-disabled="submitLoading"
                    :are-buttons-disabled="submitLoading"
                    @reset="reset"
                    @cancel="cancel"
                    @previous-step="emitPrevStep"
                    @submit-campaign="submitCampaign"
                />
            </v-card>
        </div>
        <v-card v-if="getCurrentSMSCampaign.file" class="table-container">
            <div class="card-title">{{ cardTitle }}</div>
            <array-view
                :items="smsMessages"
                :headers="headers"
                :total-pages="totalPages"
                :page="page"
                :size="size"
                :total-items="totalItems"
                :has-search-field="true"
                :update-filter="updateFilter"
                :is-fetching="isFetchingSms"
                should-rows-expand
                @paginationChange="paginationChange"
            />
        </v-card>
        <div id="export" />
    </div>
</template>

<script>
    import dateTimeUtils from '@/utils/date-time-utils'
    import smsCampaignService from '@/services/sms-campaign-service'
    import ArrayView from '@/components/ArrayView.vue'
    import CreateFormActions from '@/components/CreateFormActions.vue'
    import CampaignTable from '@/components/CampaignTable.vue'
    import tableUtils from '@/utils/tableUtils'
    export default {
        components: {
            CampaignTable,
            CreateFormActions,
            ArrayView
        },
        data() {
            return {
                submitLoading: false,
                headers: [
                    {
                        text: '#',
                        value: 'index'
                    },
                    {
                        text: 'Id',
                        value: 'id'
                    },
                    { text: 'Mobile Number', value: 'recipient' },
                    { text: 'Text', value: 'smsText' },
                    { text: 'SMS Parts', value: 'parts' },
                    { text: 'Date Sent', value: 'sentDate' },
                    { text: 'Status', value: 'status' },
                    { text: 'Date of Status', value: 'statusDate' },
                    { text: '', value: 'data-table-expand' }
                ],
                totalPages: 0,
                size: 5,
                page: 1,
                smsMessages: [],
                cardTitle: 'SMS List',
                csvString: '',
                totalItems: 0,
                smsFilter: '',
                isFetchingSms: false
            }
        },
        computed: {
            campaignTableRows() {
                return [
                    {
                        header: 'Campaign :',
                        value: this.getCurrentSMSCampaign.alias
                    },
                    {
                        header: 'Originator :',
                        value: this.getCurrentSMSCampaign.originator
                    },
                    {
                        header: 'SMS Template :',
                        value: this.getCurrentSMSCampaign.textTemplate
                    },
                    {
                        header: 'Alias :',
                        value: this.getCurrentSMSCampaign.alias
                    },
                    {
                        header: 'Created At :',
                        value: this.getHumanizedDateTime(this.getCurrentSMSCampaign.createdAt)
                    },
                    {
                        header: 'File Used :',
                        type: 'slot',
                        renderCondition: !!this.getCurrentSMSCampaign.file,
                        slot: 'file_used'
                    },
                    {
                        header: 'Scheduled At :',
                        value: this.getHumanizedDateTime(this.getCurrentSMSCampaign.scheduledAt)
                    },
                    {
                        header: 'Started At :',
                        value: 'N/A'
                    },
                    {
                        header: 'Completed At :',
                        value: 'N/A'
                    },
                    {
                        header: 'Numbers :',
                        value: this.totalItems
                    },
                    {
                        header: 'Parameters :',
                        value: this.getCurrentSMSCampaign.params
                    }
                ]
            },
            getCurrentSMSCampaign() {
                return this.$store.getters.getCurrentSMSCampaign
            },
            filter() {
                return {
                    filter: { q: this.smsFilter },
                    range: [this.page - 1, this.size]
                }
            }
        },
        watch: {
            getCurrentSMSCampaign() {
                this.initDetails()
            }
        },
        created() {
            this.initDetails()
        },
        methods: {
            async initDetails() {
                if (this.getCurrentSMSCampaign.id) {
                    await new Promise(resolve => setTimeout(resolve, 500))
                    await this.fetchAllSMSMessages()
                }
            },
            getHumanizedDateTime(dateTime) {
                return dateTimeUtils.getHumanizedDateTime(dateTime)
            },
            submitCampaign() {
                this.submitLoading = true
                return smsCampaignService
                    .submitSMSCampaign(this.getCurrentSMSCampaign.id)
                    .then(() => {
                        this.$emit('set-alert', 'Campaign Created', 'success')
                        setTimeout(() => {
                            this.submitLoading = false
                            this.$router.push(`/sms/${this.getCurrentSMSCampaign.id}`)
                        }, 3000)
                    })
                    .catch((error) => {
                        this.$emit('set-alert', error.response.data.message, 'error')
                    })
            },
            emitPrevStep() {
                this.$emit('prev-step')
            },
            reset() {
                this.$emit('reset')
            },
            cancel() {
                this.$emit('cancel')
            },
            paginationChange(page, size) {
                this.size = size
                this.page = page
                this.fetchAllSMSMessages()
            },
            fetchAllSMSMessages() {
                if (this.isFetchingSms) return

                this.isFetchingSms = true
                return smsCampaignService
                    .getSMS(this.getCurrentSMSCampaign.id, this.filter)
                    .then((resp) => {
                        this.smsMessages = resp.data.content.map((message, index) => ({
                            ...message,
                            index: index + (this.page - 1) * this.size + 1,
                            details: this.getMessageDetails(message)
                        }))
                        this.totalPages = resp.data.totalPages
                        this.totalItems = resp.data.totalElements
                    })
                    .catch((error) => {
                        this.$emit('set-alert', error.response.data.message, 'error')
                    })
                    .finally(() => {
                        this.isFetchingSms = false
                    })
            },
            downloadFile() {
                smsCampaignService
                    .downloadUploadedFile(this.getCurrentSMSCampaign.id)
                    .then((resp) => {
                        this.exportCsvFile(
                            this.getCurrentSMSCampaign.alias + '_file_used',
                            resp.data
                        )
                    })
                    .catch((error) => {
                        this.$emit('set-alert', error.response.data.message, 'error')
                    })
            },
            exportCsvFile(fileName, fileData) {
                const data = 'text;charset=utf-8,' + encodeURIComponent(fileData)
                const link = document.createElement('a')
                link.href = 'data:' + data
                link.download = `${fileName}.csv`

                const container = document.getElementById('export')
                container.appendChild(link)
                link.click()
            },
            updateFilter(newFilter) {
                this.smsFilter = newFilter
                this.fetchAllSMSMessages()
            },
            getMessageDetails(message) {
                return tableUtils.getMessageDetails(message)
            }
        }
    }
</script>

<style scoped>
.overview-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.info-container {
    display: flex;
    column-gap: 10px;
}

.table-wrapper {
    padding-bottom: 1rem;
    width: 100%;
}

.table-container {
    width: 100%;
}

.card-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 10px;
}

@media screen and (max-width: 992px) {
    .info-container {
        row-gap: 10px;
        flex-wrap: wrap;
    }
}
</style>
